
import { defineComponent } from "vue";
interface Options {
	thead?: {
		background?: string;
	};
	columns?: {
		[columnName: string]: {
			sortable?: boolean;
			filterable?: boolean;
			searchable?: boolean;
			maxWidth?: string;
			minWidth?: string;
			formatFn?: (date: Date) => string; // added to allow support for sorting dates with non-default formatting; there might be a better approach but this is flexible for future use cases
		};
	};
	rows?: {
		spacing?: string;
	};
	perPage?: number;
}

interface TableDataItem {
	[columnName: string]: any; // value will most likely be a string used for <td>
}

export interface TableData {
	columns: string[];
	tableData: TableDataItem[];
	options: Options;
}
export default defineComponent({
	name: "imdex-table",
	props: {
		title: {
			type: String,
			default: ""
		},
		value: {
			type: Boolean,
			default: false
		},
		loading: {
			type: Boolean,
			default: false
		},
		dense: {
			type: Boolean,
			default: false
		},
		highlightable: {
			type: Boolean,
			default: false
		},
		stickyFooter: {
			type: Boolean,
			default: false
		},
		stickyHeader: {
			type: Boolean,
			default: false
		},
		onPageChangeFunc: {
			type: Function as () => any,
			default: null
		},
		tableData: {
			// eslint-disable-next-line
			type: Object as () => any,
			default: () => {
				return {
					columns: [], // eslint-disable-next-line
					tableData: [],
					options: {}
				} as TableData;
			}
		}
	},
	data: () => {
		return {
			sort: 1,
			sortName: "",
			page: 1,
			perPage: 10,
			perPageSelector: false,
			searchingColumn: "",
			searchTerm: ""
		};
	},
	computed: {
		options() {
			return this.tableData.options;
		},
		hasNextPage() {
			return this.tableData.tableData.length > this.perPage * this.page;
		},
		filteredData() {
			const d = this.sort;
			let td = [...this.tableData.tableData];
			if (this.sortName != "") {
				td.sort((a, b) => {
					if (a[this.sortName] == null && b[this.sortName] == null) return 0;
					if (a[this.sortName] == null && b[this.sortName] != null) return 1;
					if (a[this.sortName] != null && b[this.sortName] == null) return -1;
					if (a[this.sortName] > b[this.sortName]) return 1 * d;
					if (a[this.sortName] < b[this.sortName]) return -1 * d;
					return 0;
				});
			}
			if (this.searchTerm != "" && this.searchingColumn != "") {
				td = td.filter((item: any) => {
					console.log(item);
					return (
						item[this.searchingColumn] &&
						item[this.searchingColumn].toString().indexOf(this.searchTerm) > -1
					);
				});
			}

			return td.slice(this.perPage * (this.page - 1), this.perPage * this.page);
		}
	},
	mounted() {
		this.perPage = this.options?.perPage || 10;
	},
	methods: {
		startSearch(col: string): any {
			this.searchingColumn = col;
			this.$nextTick(() => {
				const el: any = this.$refs[`${col}-search`];
				if (el) {
					console.log(el[0]);
					el[0].focusInput();
				}
			});
		},
		// eslint-disable-next-line
		getCurrentPageItems() {
			return this.filteredData;
		},
		checkIsSortable(column: any) {
			if (this.options == null) return true;
			if (
				this.options.columns != null &&
				Object.keys(this.options.columns).indexOf(column) > -1
			) {
				if (this.options.columns[column].sortable != null) {
					return this.options.columns[column].sortable;
				}
			} else {
				return true;
			}
		}, // eslint-disable-next-line
		checkIsFilterable(column: any) {
			if (this.options == null) return true;
			if (
				this.options.columns != null &&
				Object.keys(this.options.columns).indexOf(column) > -1
			) {
				if (this.options.columns[column].filterable != null) {
					return this.options.columns[column].filterable;
				}
			} else {
				return true;
			}
		}, // eslint-disable-next-line
		checkIsSearchable(column: any) {
			if (this.options == null) return true;
			if (
				this.options.columns != null &&
				Object.keys(this.options.columns).indexOf(column) > -1
			) {
				if (this.options.columns[column].searchable != null) {
					return this.options.columns[column].searchable;
				} else {
					return false;
				}
			} else {
				return false;
			}
		}, // eslint-disable-next-line

		getStyleFromOptions(column: any) {
			if (this.options == null) return "";
			let style = ``;
			if (
				this.options.columns != null &&
				Object.keys(this.options.columns).indexOf(column) > -1
			) {
				if (this.options.columns[column].maxWidth != null) {
					style = style.concat(`width:${this.options.columns[column].maxWidth}`);
				}
				if (this.options.columns[column].minWidth != null) {
					style = style.concat(`width:${this.options.columns[column].minWidth}`);
				}
			}
			return style;
		},
		getRowStyleFromOptions() {
			if (this.options == null) return ``;
			let style = ``;
			if (
				this.options.rows != null &&
				Object.keys(this.options.rows).indexOf("spacing") > -1
			) {
				if (this.options.rows.spacing != null) {
					style = style.concat(`padding:${this.options.rows.spacing} 8px;`);
				}
			}
			return style;
		},
		formatHeader(header: string) {
			return header.replaceAll("_", " ");
		},
		prevPage() {
			this.page = this.page - 1;

			if (this.onPageChangeFunc) {
				this.onPageChangeFunc();
			}
		},
		async nextPage() {
			this.page = this.page + 1;
			// eslint-disable-next-line
			const query: any = this.$route.query;
			query.page = this.page;
			query.limit = this.perPage;

			if (this.onPageChangeFunc) {
				this.onPageChangeFunc();
			}

			await this.$router.push({ query });
		},
		sortBy(column: string) {
			if (this.sortName == column) {
				this.sort = this.sort * -1;
			}
			this.sortName = column;
		},
		isVisible(name: string) {
			const slotlist = Object.keys({ ...this.$slots });
			return slotlist.filter((item) => item == name).length > 0;
		}
	},
	watch: {
		value(v) {
			if (v) {
				setTimeout(() => {
					this.$emit("update:value", false);
				}, 3000);
			}
		},
		filteredData(v) {
			if (!v.length && this.page > 1) {
				this.prevPage();
			}
		}
	}
});
